import { Box, Heading, Text } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { useReq } from "@larner.dev/use-req";
import { useEffect, useMemo } from "react";
import {
  getDatingIntentionLabel,
  getDatingIntentionValueCategoryName,
  MediaKey,
  UserProfile as UserProfileType,
  UserProfileWithContactInfo,
} from "@greenflagdate/shared";
import { apiReq } from "../../lib/apiReq";
import { Alert } from "../Alert/Alert";
import { NewCard } from "../Card/Card";
import { DatingIntentionIcon } from "../DatingIntentionIcon/DatingIntentionIcon";
import { FriendsOnlyNote } from "../FriendsOnlyNote/FriendsOnlyNote";
import { Loader } from "../Loader/Loader";
import { MediaImage } from "../MediaImage/MediaImage";
import { NotFound } from "../NotFound/NotFound";

interface ProfileProps {
  userId: number;
}

export const Profile = ({ userId }: ProfileProps) => {
  const [profileReq, profileReqState] = useReq<
    UserProfileType | UserProfileWithContactInfo
  >(apiReq);
  useEffect(() => {
    profileReq.get(`/user/profile/${userId}`);
  }, [profileReq, userId]);
  const mediaKeys = useMemo(
    () =>
      profileReqState.result
        ? (Object.values(MediaKey) as MediaKey[]).filter((k) => {
            return profileReqState.result!.media[k];
          })
        : [],
    [profileReqState.result]
  );
  const values = useMemo(
    () => profileReqState.result?.values.filter((v) => v),
    [profileReqState.result?.values]
  );

  return (
    <Box
      maxWidth="40rem"
      marginX="auto"
      width="100%"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      <NewCard
        border="none"
        borderWidth={0}
        flex={1}
        marginBottom="1.5rem"
        display="flex"
        flexDirection="column"
        alignItems={profileReqState.result ? "flex-start" : "center"}
        justifyContent={profileReqState.result ? "flex-start" : "center"}
      >
        {profileReqState.loading ? (
          <Loader />
        ) : profileReqState.error ? (
          <Alert
            title={
              profileReqState.error.code === "PROFILE_NOT_AVAILABLE"
                ? "You can only view profiles of your matches."
                : profileReqState.error.message
            }
          />
        ) : !profileReqState.result ? (
          <NotFound />
        ) : (
          <>
            {!!mediaKeys[0] && (
              <MediaImage
                path={profileReqState.result!.media[mediaKeys[0]]!.urls.cropped}
              />
            )}
            <Box marginY="2rem" width="100%">
              <Box display="flex" flexWrap="wrap" alignItems="flex-start">
                <Box flex={1}>
                  <Heading as="h2" textStyle="headingMd">
                    {profileReqState.result.firstName}
                  </Heading>
                  <Text fontSize="1rem">{profileReqState.result.age}</Text>
                </Box>
                {!!profileReqState.result.lastActiveWithin && (
                  <Box
                    whiteSpace="nowrap"
                    fontSize="0.625rem"
                    fontWeight={600}
                    color="brand.green.light"
                    backgroundColor="#D6EFE2"
                    padding="0.375rem"
                    borderRadius="5px"
                    display="flex"
                    alignItems="center"
                    gap="0.25rem"
                  >
                    <FontAwesomeIcon icon={faCircle} />
                    Active within the last{" "}
                    {profileReqState.result.lastActiveWithin}
                  </Box>
                )}
              </Box>
              {profileReqState.result.friendsOnly && (
                <>
                  <Heading as="h2" textStyle="headingMd" marginTop="1.5rem">
                    Looking For
                  </Heading>
                  <FriendsOnlyNote marginTop="0.5rem" />
                </>
              )}
              {!!profileReqState.result.datingIntention && (
                <>
                  <Heading as="h2" textStyle="headingMd" marginTop="1.5rem">
                    Dating Intention
                  </Heading>
                  <Box
                    borderWidth="2px"
                    borderColor="brand.green.dark"
                    borderStyle="solid"
                    borderRadius="5px"
                    paddingX="0.5rem"
                    paddingY="0.425rem"
                    width="100%"
                    alignItems="center"
                    lineHeight="100%"
                    marginTop="0.5rem"
                    display="flex"
                    justifyContent="flex-start"
                  >
                    <DatingIntentionIcon
                      intention={profileReqState.result.datingIntention}
                      height="1.6rem"
                      marginRight="0.5rem"
                    />
                    <Text fontSize="0.9rem" color="brand.green.dark">
                      {getDatingIntentionLabel(
                        profileReqState.result.datingIntention
                      )}
                    </Text>
                  </Box>
                </>
              )}
              {!!profileReqState.result.datingIntention && !!values?.length && (
                <>
                  <Heading as="h2" textStyle="headingMd" marginTop="1.5rem">
                    {getDatingIntentionValueCategoryName(
                      profileReqState.result.datingIntention
                    )}
                  </Heading>

                  {values.map(
                    (v, i) =>
                      !!v && (
                        <Box
                          key={i}
                          borderWidth="2px"
                          borderColor="brand.green.dark"
                          borderStyle="solid"
                          borderRadius="5px"
                          padding="0.5rem"
                          width="100%"
                          alignItems="center"
                          justifyContent="center"
                          lineHeight="100%"
                          marginTop="0.5rem"
                        >
                          <Text fontSize="0.9rem" color="brand.green.dark">
                            {v}
                          </Text>
                        </Box>
                      )
                  )}
                </>
              )}
            </Box>

            {!!mediaKeys.slice(1).length &&
              mediaKeys
                .slice(2)
                .map((k) => (
                  <MediaImage
                    key={k}
                    path={profileReqState.result!.media[k]!.urls.cropped}
                    marginTop="1.5rem"
                  />
                ))}

            {!mediaKeys.length && (
              <Text fontSize="1rem" marginTop="2rem">
                {profileReqState.result.firstName} hasn't completed their
                profile yet, but you can still spark a connection!
              </Text>
            )}
          </>
        )}
      </NewCard>
    </Box>
  );
};
